import type { Marble } from 'src/types/domain/Marble'
import { glyphsForKey, type BirdieTileView, type PuttMark } from './tileViewModel'

export const PATTERN_RE = /^[HM]{3}$/
/** The three rings fill in sequence, 250 ms apart (spec, Settlement row). */
export const RING_STAGGER_MS = 250

export interface SettledResult {
  marbleId: string
  marbleNumber: number
  marbleColor: string
  position: number
}

export interface SettledPayload {
  results: SettledResult[]
  marketResults?: Record<string, SettledResult[]>
}

const firstPlace = (results: readonly SettledResult[] | undefined): SettledResult | undefined =>
  results?.find((result) => result.position === 1)

/** The COUNT winner: `results` position 1 — the same read the shared panel does. */
export function countWinnerId(payload: SettledPayload | undefined): string | undefined {
  return firstPlace(payload?.results)?.marbleId
}

/**
 * The EXACT winner. The settle payload puts the count marble in `results` and the
 * pattern marble in `market_results[<exact market>]` — ALWAYS, HHH/MMM included
 * (A1). The widget does not know the exact market's id, so it takes the one
 * position-1 marble whose canonical key is a pattern.
 */
export function exactWinnerId(
  payload: SettledPayload | undefined,
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
): string | undefined {
  const marketResults = payload?.marketResults
  if (!marketResults) return undefined
  const keyById = new Map(marbles.map((marble) => [marble.uuid, marble.color.toUpperCase()]))
  for (const marketId of Object.keys(marketResults)) {
    const winner = firstPlace(marketResults[marketId])
    if (!winner) continue
    const key = keyById.get(winner.marbleId)
    if (key !== undefined && PATTERN_RE.test(key)) return winner.marbleId
  }
  return undefined
}

/**
 * The round's TYPE, as settled: 'FROST', 'FIRE' or 'PLAIN'.
 *
 * Resolved the same way the exact winner is — by canonical key rather than by market id, because the
 * widget is not told which market is which. PLAIN is a real settled outcome here and not an absence:
 * the round-type market always names a winner, and most rounds it is the one nobody could back.
 *
 * `undefined` means the round carried no round-type market at all — every round before B36, and any
 * round from an engine that does not publish one.
 */
const ROUND_TYPE_RE = /^(PLAIN|FROST|FIRE)$/

export type SettledRoundType = 'PLAIN' | 'FROST' | 'FIRE'

export function roundTypeFromSettled(
  payload: SettledPayload | undefined,
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
): SettledRoundType | undefined {
  const marketResults = payload?.marketResults
  if (!marketResults) return undefined
  const keyById = new Map(marbles.map((marble) => [marble.uuid, marble.color.toUpperCase()]))
  for (const marketId of Object.keys(marketResults)) {
    const winner = firstPlace(marketResults[marketId])
    if (!winner) continue
    const key = keyById.get(winner.marbleId)
    if (key !== undefined && ROUND_TYPE_RE.test(key)) return key as SettledRoundType
  }
  return undefined
}

/**
 * The tile a settled round type lights, or undefined when none does.
 *
 * PLAIN is the one settled type with no tile behind it — the board prices frost and fire only — so a
 * plain round lights nothing rather than lighting an option nobody could back. The tiles are the
 * host's, not the payload's: the same type over a board that never carried the market resolves to
 * nothing at all.
 */
export function roundTypeWinnerId(
  type: SettledRoundType | undefined,
  tiles: readonly Pick<BirdieTileView, 'optionId' | 'key'>[]
): string | undefined {
  if (type !== 'FROST' && type !== 'FIRE') return undefined
  return tiles.find((tile) => tile.key.toUpperCase() === type)?.optionId
}

export function patternFromSettled(
  payload: SettledPayload | undefined,
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
): string | undefined {
  const winnerId = exactWinnerId(payload, marbles)
  if (winnerId === undefined) return undefined
  const marble = marbles.find((candidate) => candidate.uuid === winnerId)
  return marble ? marble.color.toUpperCase() : undefined
}

/** The same solid/hollow vocabulary as the tiles — one language, three surfaces. */
export function circlesFor(pattern: string): PuttMark[] {
  return PATTERN_RE.test(pattern) ? (glyphsForKey(pattern) ?? []) : []
}

export function ringDelaysMs(count = 3): number[] {
  const out: number[] = []
  for (let i = 0; i < count; i++) out.push(i * RING_STAGGER_MS)
  return out
}

export interface SettlementView {
  countOptionId?: string
  exactOptionId?: string
  /** The drawn type, for the host to resolve against its own tiles (`roundTypeWinnerId`). */
  roundType?: SettledRoundType
  pattern?: string
  marks: PuttMark[]
  delaysMs: number[]
}

export function settlementView(
  payload: SettledPayload | undefined,
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
): SettlementView | undefined {
  if (!payload) return undefined
  const pattern = patternFromSettled(payload, marbles)
  const marks = pattern ? circlesFor(pattern) : []
  return {
    countOptionId: countWinnerId(payload),
    exactOptionId: exactWinnerId(payload, marbles),
    roundType: roundTypeFromSettled(payload, marbles),
    pattern,
    marks,
    delaysMs: ringDelaysMs(marks.length === 0 ? 3 : marks.length)
  }
}
